import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { useModuleEnabled } from '../navigation'

interface CreateItem {
  label: string
  icon: string
  to: string
  /** Modulet, punktet hører til. Slået fra = punktet vises ikke. */
  module?: string
  /** Hvem der må. Samme regel som knappen på selve siden. */
  allowed: boolean
}

/**
 * "+" øverst til højre: alt, man kan oprette, ét sted (Simon, 2026-09-30).
 *
 * Menuen er en genvej - den åbner siden med `?ny=<hvad>`, og siden åbner selv sin formular (lib/createRequest.ts).
 * Et punkt vises kun, når modulet er slået til, og man må det samme som på siden selv - ellers ville menuen
 * tilbyde noget, siden bagefter ikke viser.
 */
export function CreateMenu() {
  const { isProjectManager, isIntranetAdmin } = useAuth()
  const moduleEnabled = useModuleEnabled()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const items: CreateItem[] = [
    // Scan sagskassen og vælg bil eller værksted (beslutning 144) - eller en enhed eller et stykke værktøj.
    { label: 'Scan', icon: '📷', to: '/scan', allowed: true },
    { label: 'Ny sag', icon: '📁', to: '/projects?ny=sag', allowed: isProjectManager },
    { label: 'Ny servicesag', icon: '🔧', to: '/projects?ny=servicesag', module: 'service', allowed: isProjectManager },
    { label: 'Ny kunde', icon: '🏢', to: '/customers?ny=kunde', allowed: isProjectManager },
    { label: 'Ny enhed', icon: '🔋', to: '/devices?ny=enhed', module: 'service', allowed: isProjectManager },
    { label: 'Hurtig note', icon: '📝', to: '/quick-note', allowed: true },
    { label: 'Ny kvittering', icon: '🧾', to: '/receipts?ny=kvittering', module: 'receipts', allowed: true },
    { label: 'Meld skade på bil', icon: '🚗', to: '/vehicles?ny=skade', module: 'vehicles', allowed: true },
    { label: 'Ny bil', icon: '🚙', to: '/vehicles?ny=bil', module: 'vehicles', allowed: isIntranetAdmin },
    { label: 'Nyt opslag', icon: '📣', to: '/intranet?ny=opslag', module: 'intranet', allowed: isIntranetAdmin },
    { label: 'Ny begivenhed', icon: '📅', to: '/intranet?ny=begivenhed', module: 'intranet_events', allowed: true },
    { label: 'Ny afstemning', icon: '📊', to: '/intranet?ny=afstemning', module: 'intranet_polls', allowed: true },
    { label: 'Intern opgave', icon: '✅', to: '/intranet?ny=opgave', module: 'intranet_tasks', allowed: true },
    { label: 'Meld nærved-hændelse', icon: '🦺', to: '/intranet?ny=naerved', module: 'intranet_incidents', allowed: true },
    { label: 'Ny idé', icon: '💡', to: '/intranet?ny=ide', module: 'intranet_ideas', allowed: true },
    { label: 'Ros en kollega', icon: '🙌', to: '/intranet?ny=ros', module: 'intranet_kudos', allowed: true },
    { label: 'Lærlingeplan', icon: '🎓', to: '/intranet?ny=laerling', module: 'intranet_apprentices', allowed: isIntranetAdmin },
    { label: 'MUS-samtale', icon: '💬', to: '/intranet?ny=mus', module: 'intranet_mus', allowed: isProjectManager },
    { label: 'Personalemøde', icon: '🗓', to: '/intranet?ny=moede', module: 'intranet_meetings', allowed: isIntranetAdmin },
    { label: 'Fælles måltid', icon: '🍽', to: '/intranet?ny=maaltid', module: 'intranet_meals', allowed: isIntranetAdmin },
    { label: 'Køb og salg', icon: '🛒', to: '/intranet?ny=salg', module: 'intranet_market', allowed: true },
    { label: 'Ugens faglige tip', icon: '📘', to: '/intranet?ny=tip', module: 'intranet_tips', allowed: isIntranetAdmin },
    { label: 'Varer til bilen', icon: '🛻', to: '/stock/orders?ny=1', module: 'stock_van_orders', allowed: true }
  ].filter((item) => item.allowed && moduleEnabled(item.module))

  if (items.length === 0) return null

  return (
    <div className="create-menu" ref={box}>
      <button
        type="button"
        className="create-menu__button"
        aria-label="Opret"
        aria-expanded={open}
        title="Opret"
        onClick={() => setOpen((value) => !value)}
      >
        +
      </button>

      {open && (
        <div className="create-menu__list" role="menu">
          <div className="create-menu__title">Opret</div>
          {items.map((item) => (
            <button
              key={item.to}
              type="button"
              role="menuitem"
              className="create-menu__item"
              onClick={() => {
                setOpen(false)
                navigate(item.to)
              }}
            >
              <span aria-hidden>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
